import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    LinearProgress,
    MenuItem,
    Popover,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { RESOLUTION_OPTIONS } from '../../components/charts/convergence/constants';
import { estimate, gridWindow, MAX_ROWS_TOTAL } from './exportPlan';
import {
    runExport,
    saveBlob,
    type ExportDeps,
    type ExportProgress,
} from './exportRun';
import {
    CLOCK_EXPORT_HINT,
    type ClockExportFn,
    type ExportData,
    type ExportFile,
    type ExportMarket,
    type ExportSettings,
    type ExportSide,
    isClockExportConfirm,
} from './exportTypes';

type RangeChoice = 'all' | 'shown' | 'custom';
type ScopeChoice = 'shown' | 'all';

export interface ExportBoxProps {
    eventId: string;
    eventSlug: string;
    eventTitle: string;
    shownMarkets: readonly ExportMarket[];
    allMarkets: readonly ExportMarket[];
    bounds: [Date, Date];
    shownRange: [Date, Date] | null;
    defaultInterval?: number;
    loadPage: ExportDeps['loadPage'];
    isFatal?: ExportDeps['isFatal'];
    clockExport?: ClockExportFn;
    save?: (blob: Blob, filename: string) => void;
    label?: string;
    tooltip?: string;
}

type Status =
    | { kind: 'idle' }
    | { kind: 'running'; progress: ExportProgress }
    | { kind: 'done'; filename: string; rows: number }
    // The clock download is big and waits for a yes.
    | { kind: 'confirm'; megabytes: number }
    | { kind: 'error'; message: string };

const groupSx = {
    height: 26,
    '& .MuiToggleButton-root': {
        p: 0,
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'none' as const,
        borderColor: 'divider',
    },
};

const DAY = 86_400_000;

function dayOf(date: Date): string {
    return date.toISOString().slice(0, 10);
}

export function Row({
    label,
    hint,
    children,
}: {
    label: string;
    hint?: string;
    children: ReactNode;
}) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '92px minmax(0, 1fr)',
                alignItems: 'center',
                columnGap: 1,
                minHeight: 30,
            }}
        >
            <Tooltip title={hint ?? ''} placement="left" arrow describeChild>
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                        cursor: hint ? 'help' : undefined,
                        width: 'fit-content',
                    }}
                >
                    {label}
                </Typography>
            </Tooltip>
            <Box sx={{ minWidth: 0 }}>{children}</Box>
        </Box>
    );
}

export interface ChoiceOption<T extends string> {
    value: T;
    label: string;
    hint: string;
}

export function Choice<T extends string>({
    value,
    options,
    onChange,
    disabled,
    label,
}: {
    value: T;
    options: readonly ChoiceOption<T>[];
    onChange: (value: T) => void;
    disabled: boolean;
    label: string;
}) {
    return (
        <ToggleButtonGroup
            size="small"
            exclusive
            value={value}
            disabled={disabled}
            aria-label={label}
            onChange={(_, next: T | null) => {
                if (next) onChange(next);
            }}
            sx={groupSx}
        >
            {options.map(o => (
                <ToggleButton key={o.value} value={o.value}>
                    <Tooltip
                        title={o.hint}
                        arrow
                        enterDelay={300}
                        describeChild
                    >
                        <Box component="span" sx={{ px: 1, py: '2px' }}>
                            {o.label}
                        </Box>
                    </Tooltip>
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
}

export function ExportBox(props: ExportBoxProps) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [scope, setScope] = useState<ScopeChoice>('shown');
    const [rangeChoice, setRangeChoice] = useState<RangeChoice>('all');
    const [customFrom, setCustomFrom] = useState<string | null>(null);
    const [customTo, setCustomTo] = useState<string | null>(null);
    const [interval, setInterval] = useState<number | null>(null);
    const [side, setSide] = useState<ExportSide>('merged');
    const [data, setData] = useState<ExportData>('candles');
    const isClock = data === 'clock' && props.clockExport !== undefined;
    useEffect(() => {
        if (data === 'clock' && !props.clockExport) setData('candles');
    }, [data, props.clockExport]);
    const [file, setFile] = useState<ExportFile>('csv');
    const [fill, setFill] = useState(true);
    const [status, setStatus] = useState<Status>({ kind: 'idle' });
    const controller = useRef<AbortController | null>(null);

    useEffect(() => () => controller.current?.abort(), []);

    const open = anchor !== null;
    const running = status.kind === 'running';
    const intervalMinutes = interval ?? props.defaultInterval ?? 60;
    const canScope = props.allMarkets.length > props.shownMarkets.length;
    const markets =
        scope === 'all' && canScope ? props.allMarkets : props.shownMarkets;

    const fromDay = customFrom ?? dayOf(props.bounds[0]);
    const toDay = customTo ?? dayOf(props.bounds[1]);
    const ownSpans = markets.some(
        m => m.fromMs !== undefined || m.toMs !== undefined
    );
    const eventCount = new Set(markets.map(m => m.event?.id ?? '')).size;

    const boundsFrom = props.bounds[0].getTime();
    const boundsTo = props.bounds[1].getTime();
    const shownFrom = props.shownRange?.[0].getTime() ?? null;
    const shownTo = props.shownRange?.[1].getTime() ?? null;
    const window = useMemo(() => {
        let from = boundsFrom;
        let to = boundsTo;
        if (rangeChoice === 'shown' && shownFrom !== null && shownTo !== null) {
            from = shownFrom;
            to = shownTo;
        }
        if (rangeChoice === 'custom') {
            from = Date.parse(`${fromDay}T00:00:00Z`);
            to = Date.parse(`${toDay}T00:00:00Z`) + DAY;
        }
        if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
            return null;
        }
        // The Event clock grids the range itself, with its own frequency; the
        // box's interval must not widen what it gets.
        return gridWindow(from, to, isClock ? 1 : intervalMinutes, Date.now());
    }, [
        rangeChoice,
        boundsFrom,
        boundsTo,
        shownFrom,
        shownTo,
        fromDay,
        toDay,
        intervalMinutes,
        isClock,
    ]);

    const oneFile =
        markets.length === 1 || (data === 'prices' && eventCount <= 1);
    const plan = estimate(window, intervalMinutes, markets, oneFile);
    const files =
        data === 'prices' && eventCount > 1 && plan.files > 0
            ? eventCount
            : plan.files;

    // A question about the size belongs to the outcomes and the range it was
    // asked for.
    const clockScope = `${markets.map(m => m.conditionId).join(',')}|${window?.startMs}|${window?.endMs}`;
    useEffect(() => {
        setStatus(prev => (prev.kind === 'confirm' ? { kind: 'idle' } : prev));
    }, [clockScope, isClock]);

    const start = async (allowLarge = false) => {
        if (!window || (!isClock && plan.blocked)) return;
        if (isClock) {
            const abort = new AbortController();
            controller.current = abort;
            setStatus({ kind: 'running', progress: { done: 0, total: 1 } });
            try {
                const result = await props.clockExport!({
                    markets,
                    fromMs: window.startMs,
                    toMs: window.endMs,
                    eventSlug: props.eventSlug,
                    eventTitle: props.eventTitle,
                    signal: abort.signal,
                    allowLarge,
                    onProgress: progress => {
                        if (!abort.signal.aborted) {
                            setStatus({ kind: 'running', progress });
                        }
                    },
                });
                if (abort.signal.aborted) return;
                (props.save ?? saveBlob)(result.blob, result.filename);
                setStatus({
                    kind: 'done',
                    filename: result.filename,
                    rows: result.rows,
                });
            } catch (error) {
                if (abort.signal.aborted) return;
                if (isClockExportConfirm(error)) {
                    setStatus({
                        kind: 'confirm',
                        megabytes: error.megabytes,
                    });
                    return;
                }
                console.error('event clock download failed:', error);
                setStatus({
                    kind: 'error',
                    message:
                        error instanceof Error
                            ? error.message
                            : 'The event clock could not be computed.',
                });
            }
            return;
        }
        const settings: ExportSettings = {
            eventId: props.eventId,
            eventSlug: props.eventSlug,
            eventTitle: props.eventTitle,
            markets,
            fromMs: window.startMs,
            toMs: window.endMs,
            intervalMinutes,
            side,
            data,
            file,
            fill,
        };
        const abort = new AbortController();
        controller.current = abort;
        setStatus({
            kind: 'running',
            progress: { done: 0, total: plan.requests },
        });
        try {
            const result = await runExport(settings, window, {
                loadPage: props.loadPage,
                isFatal: props.isFatal,
                signal: abort.signal,
                onProgress: progress => {
                    if (!abort.signal.aborted) {
                        setStatus({ kind: 'running', progress });
                    }
                },
            });
            if (abort.signal.aborted) return;
            (props.save ?? saveBlob)(result.blob, result.filename);
            setStatus({
                kind: 'done',
                filename: result.filename,
                rows: result.rows,
            });
        } catch (error) {
            if (abort.signal.aborted) return;
            console.error('download failed:', error);
            setStatus({
                kind: 'error',
                message:
                    props.isFatal?.(error) === true
                        ? 'The server function is not up to date. Deploy loadEventPrices first.'
                        : 'The data could not be loaded.',
            });
        }
    };

    const cancel = () => {
        controller.current?.abort();
        setStatus({ kind: 'idle' });
    };

    const blockedText =
        plan.blocked === 'total'
            ? `About ${plan.rowsTotal.toLocaleString('en-US')} rows. More than ${MAX_ROWS_TOTAL.toLocaleString('en-US')} rows is not possible in one download. Select fewer outcomes, a shorter time range or a longer interval.`
            : plan.blocked === 'empty'
              ? 'This time range has no data.'
              : null;

    const percent = running
        ? status.progress.total > 0
            ? (status.progress.done / status.progress.total) * 100
            : 0
        : 0;

    return (
        <>
            <Tooltip title={props.tooltip ?? 'Download data'} describeChild>
                <span>
                    {props.label ? (
                        <Button
                            size="small"
                            aria-label="Download data"
                            disabled={props.shownMarkets.length === 0}
                            onClick={e => setAnchor(e.currentTarget)}
                            startIcon={
                                <DownloadIcon
                                    sx={{ fontSize: '14px !important' }}
                                />
                            }
                            sx={{
                                fontSize: '10px',
                                minWidth: 0,
                                py: 0.25,
                                px: 0.5,
                                '& .MuiButton-startIcon': { mr: 0.25 },
                            }}
                        >
                            {props.label}
                        </Button>
                    ) : (
                        <IconButton
                            size="small"
                            aria-label="Download data"
                            disabled={props.shownMarkets.length === 0}
                            onClick={e => setAnchor(e.currentTarget)}
                            sx={{ p: 0.5 }}
                        >
                            <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                </span>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 380, p: 2 } } }}
            >
                <Box data-testid="export-box">
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mb: 1,
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 18 }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                            Download data
                        </Typography>
                    </Box>

                    {canScope && (
                        <Row
                            label="Outcomes"
                            hint="The markets that go into the download."
                        >
                            <Choice
                                label="Outcomes"
                                value={scope}
                                disabled={running}
                                onChange={setScope}
                                options={[
                                    {
                                        value: 'shown',
                                        label: `Selected (${props.shownMarkets.length})`,
                                        hint: 'Only the outcomes that are on the chart now.',
                                    },
                                    {
                                        value: 'all',
                                        label: `All of the event (${props.allMarkets.length})`,
                                        hint: 'Each outcome of this event, also the outcomes that are not on the chart.',
                                    },
                                ]}
                            />
                        </Row>
                    )}

                    <Row
                        label="Time range"
                        hint="The time span of the data. Times are in UTC."
                    >
                        <Choice
                            label="Time range"
                            value={rangeChoice}
                            disabled={running}
                            onChange={setRangeChoice}
                            options={[
                                {
                                    value: 'all',
                                    label: 'All',
                                    hint: ownSpans
                                        ? 'From the start of each market to now, or to its end.'
                                        : 'From the start of the event to now, or to its end.',
                                },
                                ...(props.shownRange
                                    ? [
                                          {
                                              value: 'shown' as const,
                                              label: 'Selected range',
                                              hint: 'The time range that the chart shows now.',
                                          },
                                      ]
                                    : []),
                                {
                                    value: 'custom',
                                    label: 'Custom',
                                    hint: 'Your own start day and end day. The end day is included.',
                                },
                            ]}
                        />
                    </Row>
                    {rangeChoice === 'custom' && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                columnGap: 1.5,
                                mt: 0.5,
                                mb: 1,
                            }}
                        >
                            {(
                                [
                                    ['From', fromDay, setCustomFrom],
                                    ['To', toDay, setCustomTo],
                                ] as const
                            ).map(([label, value, set]) => (
                                <Box key={label}>
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            color: 'text.secondary',
                                            mb: 0.25,
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                    <TextField
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={value}
                                        disabled={running}
                                        onChange={e => set(e.target.value)}
                                        inputProps={{
                                            'aria-label': `${label} date`,
                                            style: {
                                                fontSize: '12px',
                                                padding: '6px 8px',
                                            },
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Row
                        label="Interval"
                        hint="The time that one candle covers."
                    >
                        <Select
                            value={intervalMinutes}
                            disabled={running || isClock}
                            size="small"
                            onChange={e => setInterval(Number(e.target.value))}
                            inputProps={{ 'aria-label': 'Interval' }}
                            sx={{
                                height: 26,
                                fontSize: '11px',
                                fontWeight: 600,
                                '& .MuiSelect-select': { py: '2px', px: 1 },
                            }}
                        >
                            {RESOLUTION_OPTIONS.map(option => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    sx={{ fontSize: '12px' }}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Row>

                    <Row
                        label="Side"
                        hint="Each market has a YES side and a NO side. Each side has its own trades."
                    >
                        <Choice
                            label="Side"
                            value={side}
                            disabled={running || isClock}
                            onChange={setSide}
                            options={[
                                {
                                    value: 'merged',
                                    label: 'Merged',
                                    hint: 'YES and NO trades in one candle, as a YES price. A NO price is counted as 1 minus the price. Volume and trade counts stay apart for each side. In a minute with trades on both sides, YES counts as first and NO as last.',
                                },
                                {
                                    value: 'yes',
                                    label: 'YES only',
                                    hint: 'Only the trades on the YES side, at the YES price.',
                                },
                                {
                                    value: 'no',
                                    label: 'NO only',
                                    hint: 'Only the trades on the NO side, at the NO price.',
                                },
                            ]}
                        />
                    </Row>

                    <Row label="Data" hint="What each row of the file holds.">
                        <Choice
                            label="Data"
                            value={data}
                            disabled={running}
                            onChange={setData}
                            options={[
                                {
                                    value: 'candles',
                                    label: 'Candles',
                                    hint: 'Open, high, low and close for each interval, with volume and trade counts. One file for each market.',
                                },
                                {
                                    value: 'prices',
                                    label: 'Prices',
                                    hint:
                                        eventCount > 1
                                            ? 'Only the close price for each interval. One file for each event, with one column for each outcome.'
                                            : 'Only the close price for each interval. One file, with one column for each outcome.',
                                },
                                ...(props.clockExport
                                    ? [
                                          {
                                              value: 'clock' as const,
                                              label: 'Event clock',
                                              hint: CLOCK_EXPORT_HINT,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </Row>
                    {isClock && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'text.secondary',
                                mt: 0.5,
                            }}
                        >
                            Interval, side and fill follow your Event clock
                            settings.
                        </Typography>
                    )}

                    <Row label="File" hint="The format of the file.">
                        <Choice
                            label="File"
                            value={file}
                            disabled={running || isClock}
                            onChange={setFile}
                            options={[
                                {
                                    value: 'csv',
                                    label: 'CSV',
                                    hint: 'A table as text. Opens in Excel and in pandas.',
                                },
                                {
                                    value: 'json',
                                    label: 'JSON',
                                    hint: 'Structured data with the download settings at the top.',
                                },
                            ]}
                        />
                    </Row>

                    <Row label="">
                        <Tooltip
                            title="A time step with no trade gets the last close as its price and zero volume. Without this, the file has only time steps with trades."
                            placement="left"
                            arrow
                            describeChild
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={fill}
                                        disabled={running || isClock}
                                        onChange={e =>
                                            setFill(e.target.checked)
                                        }
                                        sx={{ py: 0.25 }}
                                    />
                                }
                                label="Fill empty candles"
                                slotProps={{
                                    typography: { sx: { fontSize: '12px' } },
                                }}
                            />
                        </Tooltip>
                    </Row>

                    <Typography
                        data-testid="export-estimate"
                        sx={{
                            fontSize: '11px',
                            mt: 1,
                            color:
                                blockedText && !isClock
                                    ? 'error.main'
                                    : 'text.secondary',
                        }}
                    >
                        {isClock
                            ? `${markets.length} ${markets.length === 1 ? 'outcome' : 'outcomes'}: prices used, clock path, results and reproduce.py in one zip.`
                            : blockedText ??
                              `About ${plan.rowsTotal.toLocaleString('en-US')} rows, ${files} ${files === 1 ? 'file' : 'files in one zip'}, ${plan.requests} ${plan.requests === 1 ? 'request' : 'requests'}.`}
                    </Typography>

                    {running && (
                        <Box sx={{ mt: 1.5 }}>
                            <LinearProgress
                                variant="determinate"
                                value={percent}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    mt: 0.5,
                                }}
                            >
                                Loading {status.progress.done} of{' '}
                                {status.progress.total}
                            </Typography>
                        </Box>
                    )}
                    {status.kind === 'done' && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'success.main',
                                mt: 1,
                                wordBreak: 'break-all',
                            }}
                        >
                            Done: {status.rows.toLocaleString('en-US')} rows in{' '}
                            {status.filename}
                        </Typography>
                    )}
                    {status.kind === 'confirm' && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'text.primary',
                                mt: 1,
                            }}
                        >
                            {`This download is about ${status.megabytes} MB. Download anyway?`}
                        </Typography>
                    )}
                    {status.kind === 'error' && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'error.main',
                                mt: 1,
                            }}
                        >
                            {status.message}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            mt: 1.5,
                        }}
                    >
                        {running ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={cancel}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={<DownloadIcon />}
                                disabled={
                                    isClock
                                        ? window === null ||
                                          markets.length === 0
                                        : plan.blocked !== null
                                }
                                onClick={() => start(status.kind === 'confirm')}
                                sx={{ textTransform: 'none' }}
                            >
                                {status.kind === 'error'
                                    ? 'Try again'
                                    : status.kind === 'confirm'
                                      ? 'Download anyway'
                                      : 'Download'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
